/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable import/no-anonymous-default-export */
// @TODO: Check this
import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'

/**
 * Selects the settings state from the Redux store.
 *
 * @param {Object} state - The Redux store state.
 * @returns {Object} The settings state.
 */
const getSettings = createSelector(
    (state) => state.settings,
    (settings) => settings
)

/**
 * Selector to retrieve the reason for reloading the page from the settings state.
 *
 * @param {Object} state - The global state object.
 * @returns {string} The reason for reloading the page from the settings.
 */
const getSettingsReloadPageReason = createSelector(
    (state) => state.settings,
    (settings) => settings.status.reload_page_reason
)

/**
 * Retrieves the list of supported countries from the application settings.
 *
 * @param {Object} state - The current state of the application.
 * @returns {Array} The list of countries supported by the application.
 */
const getCountriesList = createSelector(
    (state) => state.settings,
    (settings) => settings.countries_support.countries
)

/**
 * Selects the default country that is enabled from the settings state.
 *
 * @param {Object} state - The application state.
 * @returns {Object|undefined} The default enabled country object or undefined if not found.
 */
const getDefaultCountry = createSelector(
    (state) => state.settings,
    (settings) =>
        settings.countries_support.countries.find(
            (country) => country.is_default && country.is_enabled
        )
)

/**
 * Selects and returns a list of available countries from the state settings where countries are enabled and are present
 * in the user profiles list.
 *
 * @param {Object} state - The state object containing settings.
 * @returns {Array} A list of available countries that are enabled and have a corresponding user profile.
 */
const getAvilableFormCountries = createSelector(
    (state) => state.settings,
    (settings) => {
        const countriesList = settings.countries_support.countries
        const userProfilesList = settings.countries_support.user_profiles
        const availableCountries = countriesList.filter(
            (countryItem) =>
                countryItem.is_enabled &&
                userProfilesList.indexOf(countryItem.code) != -1
        )

        return availableCountries
    }
)

/**
 * Retrieves the list of countries supported for user profiles from the state settings.
 *
 * @param {Object} state - The Redux state object containing settings.
 * @returns {Array} The array of countries supported for user profiles.
 */
const getUserProfileCountries = createSelector(
    (state) => state.settings,
    (settings) => settings.countries_support.user_profiles
)

/**
 * Selects and filters countries based on audience type support codes and user-provided countries.
 *
 * @param {Object} state - The state object containing settings.
 * @param {Array} userCountries - An array of user-specified country codes.
 * @returns {Array} A filtered list of countries that are supported for audience targeting, considering user preference.
 */
const getAudienceTypeCountries = createSelector(
    (state, userCountries) => ({ settings: state.settings, userCountries }),
    ({ settings, userCountries }) => {
        const codes =
            settings.countries_support.campaign_type.audience_target.list
        const countries = settings.countries_support.countries

        const res = countries.filter(
            (country) => codes.indexOf(country.code) != -1
        )
        if (userCountries !== null) {
            res = countries.filter(
                (country) => userCountries.indexOf(country.code) != -1
            )
        }

        return res
    }
)

/**
 * Selector to get geofence countries based on the settings and user-specified countries.
 *
 * @param {Object} state - The current state of the application.
 * @param {Array<string>} userCountries - An array of country codes specified by the user.
 * @returns {Array<Object>} An array of country objects that are supported by the geofence settings and match userCountries.
 */
const getGeofenceCountries = createSelector(
    (state, userCountries) => ({ settings: state.settings, userCountries }),
    ({ settings, userCountries }) => {
        const codes = settings.countries_support.campaign_type.geofence.list
        const countries = settings.countries_support.countries

        const res = countries.filter(
            (country) => codes.indexOf(country.code) != -1
        )
        if (userCountries !== null) {
            res = countries.filter(
                (country) => userCountries.indexOf(country.code) != -1
            )
        }

        return res
    }
)

/**
 * Selector to retrieve the environment setting from the state.
 *
 * @param {Object} state - The Redux state.
 * @returns {string} The environment setting from the state.
 */
const getEnv = createSelector(
    (state) => state.settings,
    (settings) => settings.general.env
)

/**
 * Selector to determine if the printing process is ongoing.
 *
 * @param {Object} state - The Redux state.
 * @returns {boolean} Returns `true` if printing is in progress, otherwise `false`.
 */
const isPrinting = createSelector(
    (state) => state.settings,
    (settings) => settings.print.is_printing
)

/**
 * Selector to retrieve the regional settings from the state.
 *
 * @param {Object} state - The Redux state object.
 * @returns {Object} The regional settings from the settings state.
 */
const regionalSettings = createSelector(
    (state) => state.settings,
    (settings) => settings.regional_settings
)

export default {
    regionalSettings: () => useSelector(regionalSettings),
    getEnv: () => useSelector(getEnv),
    getSettings: () => useSelector(getSettings),
    isPrinting: () => useSelector(isPrinting),
    getSettingsReloadPageReason: () => useSelector(getSettingsReloadPageReason),
    getCountriesList: () => useSelector(getCountriesList),
    getDefaultCountry: () => useSelector(getDefaultCountry),
    getAvilableFormCountries: () => useSelector(getAvilableFormCountries),
    getUserProfileCountries: () => useSelector(getUserProfileCountries),

    getAudienceTypeCountries: (userCountries) =>
        useSelector((state) => getAudienceTypeCountries(state, userCountries)),
    getGeofenceCountries: (userCountries) =>
        useSelector((state) => getGeofenceCountries(state, userCountries))
}
